<script setup>
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import FileUpload from 'vue-upload-component';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import DatePicker from 'vue-datepicker-next';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import AudienceBuilder from 'dashboard/components-next/Campaigns/AudienceBuilder.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  inboxes: useMapGetter('inboxes/getAPIInboxes'),
  labels: useMapGetter('labels/getLabels'),
  globalConfig: useMapGetter('globalConfig/get'),
};

const initialState = {
  title: '',
  message: '',
  attachedFiles: [],
  inboxId: null,
  scheduledAt: null,
  postSendActions: [],
  // Submission payload (IDs only, snake_case filters) - emitted by AudienceBuilder
  audiencePayload: {
    labels: [],
    contacts: [],
    contact_filters: [],
    conversation_filters: [],
  },
  estimate: { total: 0, error: null },
};

const state = reactive({ ...initialState });
const uploadAttachment = ref(null);
const accountId = useMapGetter('getCurrentAccountId');

const audienceBuilderEnabled = computed(() => {
  const flag = formState.globalConfig?.value?.audienceBuilderEnabled;
  return flag !== false;
});

// initialAudience for AudienceBuilder (only used for edit mode, empty for create)
// AudienceBuilder manages its own internal state; we only capture the final payload
const initialAudience = ref({
  labels: [],
  contacts: [],
  contactFilters: [],
  conversationFilters: [],
});

// File upload integration (generic Active Storage direct upload)
// Matches vue-upload-component signature: (newFile, oldFile, prevent)
const onFileUpload = (newFile /* , oldFile, prevent */) => {
  try {
    if (!newFile || !newFile.file) {
      return;
    }

    useAlert(t('CAMPAIGN.WHATSAPP_EVO.UPLOAD.UPLOADING'));

    // NOTE: DirectUpload automatically sets X-CSRF-Token from meta tags.
    // Avoid setting it manually to prevent duplicate header values which cause 422.
    const upload = new DirectUpload(
      newFile.file,
      `/api/v1/accounts/${accountId.value}/campaigns/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          setDirectUploadAuthHeaders(xhr);
        },
      }
    );

    upload.create((error, blob) => {
      if (error) {
        useAlert(
          t('CAMPAIGN.WHATSAPP_EVO.UPLOAD.ERROR_WITH_REASON', {
            reason: String(error),
          })
        );
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(newFile.file);
      reader.onloadend = () => {
        state.attachedFiles = [
          ...state.attachedFiles,
          {
            resource: blob,
            isPrivate: false,
            thumb: reader.result,
            blobSignedId: blob?.signed_id,
          },
        ];
        useAlert(t('CAMPAIGN.WHATSAPP_EVO.UPLOAD.SUCCESS'));
      };
    });
  } catch (e) {
    useAlert(e?.message || t('CAMPAIGN.WHATSAPP_EVO.UPLOAD.ERROR'));
  }
};

// Custom validator for audience payload: at least one source must be present
const hasAudienceSource = value => {
  if (!value) return false;
  const { labels, contacts, contact_filters, conversation_filters } = value;

  const hasLabels = Array.isArray(labels) && labels.length > 0;
  const hasContacts = Array.isArray(contacts) && contacts.length > 0;
  if (hasLabels || hasContacts) return true;

  const hasValidFilters = filters => {
    if (!Array.isArray(filters) || filters.length === 0) return false;
    return filters.some(filter => {
      const op = filter.filter_operator;
      if (['is_present', 'is_not_present'].includes(op)) return true;
      if (!filter.values) return false;
      if (Array.isArray(filter.values)) {
        return filter.values.length > 0;
      }
      return (
        filter.values !== '' &&
        filter.values !== null &&
        filter.values !== undefined
      );
    });
  };

  return (
    hasValidFilters(contact_filters) || hasValidFilters(conversation_filters)
  );
};

const validationNow = ref(Date.now());

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: {
    required,
    isFutureDateTime: value => new Date(value).getTime() > validationNow.value,
  },
  audiencePayload: { hasAudienceSource },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const scheduledAtDate = computed({
  get: () => (state.scheduledAt ? new Date(state.scheduledAt) : null),
  set: value => {
    state.scheduledAt = value ? format(value, "yyyy-MM-dd'T'HH:mm") : null;
  },
});

const disabledDate = date => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date < today;
};

const disabledTime = date => date.getTime() <= Date.now();

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const labelOptions = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const getErrorMessage = (field, errorKey) => {
  if (!v$.value[field].$error) return '';
  switch (errorKey) {
    case 'TITLE':
      return t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.TITLE.ERROR');
    case 'MESSAGE':
      return t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.MESSAGE.ERROR');
    case 'INBOX':
      return t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.INBOX.ERROR');
    case 'SCHEDULED_AT':
      return t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.SCHEDULED_AT.ERROR');
    case 'AUDIENCE':
      return t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.AUDIENCE.ERROR');
    default:
      return '';
  }
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('audiencePayload', 'AUDIENCE'),
}));

const scheduledAtInputId = 'whatsapp-evo-scheduled-at';
const scheduledAtErrorId = 'whatsapp-evo-scheduled-at-error';
const scheduledAtInputAttrs = computed(() => ({
  id: scheduledAtInputId,
  'aria-invalid': formErrors.value.scheduledAt ? 'true' : 'false',
  ...(formErrors.value.scheduledAt && {
    'aria-describedby': scheduledAtErrorId,
  }),
}));

const isSubmitDisabled = computed(() => {
  // Re-use the validator logic to ensure consistency
  const hasAudience = hasAudienceSource(state.audiencePayload);
  // [PROSPEK] 2026-08-03 Keep estimate failures informational at submission time.
  const hasValidEstimate = audienceBuilderEnabled.value
    ? state.estimate.total > 0
    : true;
  // [/PROSPEK]

  return v$.value.$invalid || !hasAudience || !hasValidEstimate;
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const removeAttachment = index => {
  state.attachedFiles = state.attachedFiles.filter((_, i) => i !== index);
};

// Store the payload emitted by AudienceBuilder (IDs + snake_case filters)
// Do NOT feed this back to the component - it manages its own internal state
const handleAudienceUpdate = payload => {
  state.audiencePayload = payload;
};

const handleEstimateUpdate = payload => {
  state.estimate = payload;
};

const handleActionsUpdate = actions => {
  state.postSendActions = actions;
};

const cloneAudiencePayload = payload => JSON.parse(JSON.stringify(payload));

const buildDeliveryOptions = () => {
  const labelAction = state.postSendActions.find(
    action => action.type === 'add_label'
  );
  const labels = Array.isArray(labelAction?.labels) ? labelAction.labels : [];
  const [labelId] = labels;
  if (!labelId) return null;
  return {
    apply_conversation_label: true,
    conversation_label_id: labelId,
  };
};

const prepareCampaignDetails = () => {
  const deliveryOptions = buildDeliveryOptions();
  const payload = {
    title: state.title,
    message: state.message,
    attachments: state.attachedFiles
      .map(file => file.blobSignedId)
      .filter(Boolean),
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: cloneAudiencePayload(state.audiencePayload),
  };

  if (deliveryOptions) {
    payload.delivery_options = deliveryOptions;
  }

  return payload;
};

const handleSubmit = async () => {
  validationNow.value = Date.now();
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <div class="flex flex-col">
    <form class="w-full space-y-4" @submit.prevent="handleSubmit">
      <section class="space-y-4">
        <Input
          v-model="state.title"
          :label="t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.TITLE.LABEL')"
          :placeholder="
            t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.TITLE.PLACEHOLDER')
          "
          :message="formErrors.title"
          :message-type="formErrors.title ? 'error' : 'info'"
        />

        <div class="space-y-3">
          <TextArea
            v-model="state.message"
            :label="t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.MESSAGE.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.MESSAGE.PLACEHOLDER')
            "
            :message="formErrors.message"
            :message-type="formErrors.message ? 'error' : 'info'"
            rows="4"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.MESSAGE_TIP') }}
          </p>

          <!-- File Upload Section -->
          <div class="space-y-1">
            <div class="flex justify-start">
              <FileUpload
                ref="uploadAttachment"
                input-id="campaignMediaAttachment"
                :size="4096 * 4096"
                :accept="ALLOWED_FILE_TYPES"
                multiple
                :drop-directory="false"
                :data="{
                  direct_upload_url: `/api/v1/accounts/${accountId}/campaigns/direct_uploads`,
                  direct_upload: true,
                }"
                class="p-px"
                @input-file="onFileUpload"
              >
                <Button
                  icon="i-lucide-paperclip"
                  color="slate"
                  size="sm"
                  type="button"
                  class="!w-auto !px-3"
                  :label="t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.ATTACH_MEDIA')"
                />
              </FileUpload>
            </div>
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.ATTACH_MEDIA_INFO') }}
            </p>
          </div>

          <!-- Attachment Preview -->
          <AttachmentPreview
            v-if="state.attachedFiles.length > 0"
            :attachments="state.attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
      </section>

      <section class="space-y-3">
        <div class="space-y-1">
          <label for="inbox" class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.INBOX.LABEL') }}
          </label>
          <ComboBox
            id="inbox"
            v-model="state.inboxId"
            :options="inboxOptions"
            :has-error="!!formErrors.inbox"
            :placeholder="
              t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.INBOX.PLACEHOLDER')
            "
            :message="formErrors.inbox"
            class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          />
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.AUDIENCE.LABEL') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.AUDIENCE_BUILDER.HELPER') }}
          </p>
          <AudienceBuilder
            v-if="audienceBuilderEnabled"
            :inbox-id="state.inboxId"
            :initial-audience="initialAudience"
            @update:audience="handleAudienceUpdate"
            @update:estimate="handleEstimateUpdate"
            @update:actions="handleActionsUpdate"
          />
          <div v-else class="space-y-2">
            <TagMultiSelectComboBox
              v-model="state.audiencePayload.labels"
              :options="labelOptions"
              :placeholder="t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.PLACEHOLDER')"
              :has-error="!!formErrors.audience"
              :message="formErrors.audience"
              class="[&>div>button]:bg-n-alpha-black2"
            />
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.AUDIENCE_BUILDER.LABELS.HINT') }}
            </p>
          </div>
          <p v-if="formErrors.audience" class="text-xs text-n-red-11 mt-1">
            {{ formErrors.audience }}
          </p>
        </div>
      </section>

      <section class="space-y-1">
        <label
          :for="scheduledAtInputId"
          class="block text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.SCHEDULED_AT.LABEL') }}
        </label>
        <DatePicker
          v-model:value="scheduledAtDate"
          type="datetime"
          format="YYYY-MM-DD HH:mm"
          :disabled-date="disabledDate"
          :disabled-time="disabledTime"
          :input-attr="scheduledAtInputAttrs"
          :placeholder="
            t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
          "
        />
        <p
          v-if="formErrors.scheduledAt"
          :id="scheduledAtErrorId"
          class="text-xs text-n-red-11 mt-1"
        >
          {{ formErrors.scheduledAt }}
        </p>
      </section>

      <div
        class="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-end"
      >
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.BUTTONS.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3 sm:w-auto"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="t('CAMPAIGN.WHATSAPP_EVO.CREATE.FORM.BUTTONS.CREATE')"
          class="w-full sm:w-auto"
          :is-loading="isCreating"
          :disabled="isCreating || isSubmitDisabled"
        />
      </div>
    </form>
  </div>
</template>
